/* eslint-disable import/max-dependencies */
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useHandleRequest } from "@fas/cpa-state-manager/redux/hooks";
import { DASHBOARD_TABLE } from "@fas/cpa-state-manager/redux/constants";
import {
  getTableData,
  getTableFilters,
} from "@fas/cpa-state-manager/services/selectors";
import { setTableData } from "@fas/cpa-state-manager/redux/actions";
import { changeTableFilters } from "@fas/cpa-state-manager/redux/actions/table/actions";
import type { Option } from "@fas/cpa-cabinet-ui";
import type { Filters } from "@fas/cpa-cabinet-ui/lib/Table/Table.types";
import dayjs from "dayjs";
import type { Preset } from "@fas/cpa-cabinet-ui/lib/DateRangePicker/DateRangePicker.types";
import type { DashboardItemType } from "../../services/deprecatedApi";
import { getDashboardData } from "../../services/deprecatedApi";
import { defaultPreset, presets } from "./presets";

function getGroupByDateRange(from: string, to: string): string {
  const diff: number = Math.abs(dayjs(from).diff(dayjs(to), "day"));
  if (diff > 31) {
    return "month";
  }
  return "day";
}

export type UseChartHook = {
  data: DashboardItemType[];
  period: string;
  periods: Option[];
  onChangePeriod: (arg0: string) => unknown;
  isLoading: boolean;
};
export const useChart: () => UseChartHook = () => {
  const dispatch = useDispatch();
  const { isLoading, changeLoading, onFail, onFinally } =
    useHandleRequest(DASHBOARD_TABLE);
  const data = useSelector(
    (state) => getTableData(state, DASHBOARD_TABLE) as DashboardItemType[]
  );
  const filters = useSelector(
    (state): Filters => getTableFilters(state, DASHBOARD_TABLE)
  );

  function loadData() {
    changeLoading(true);

    getDashboardData({
      filters: {
        date: [{ from: filters.dateFrom, to: filters.dateTo }],
      },
      group: [getGroupByDateRange(filters.dateFrom, filters.dateTo)],
    })
      .then((items: DashboardItemType[]) => {
        dispatch(setTableData(DASHBOARD_TABLE, items));
      })
      .catch(onFail)
      .finally(onFinally);
  }

  useEffect(() => {
    loadData();
  }, [filters]);

  const onChangePeriod = (newPeriod: string) => {
    const selectedPeriod: Preset =
      presets.find(({ value }) => newPeriod === value) || defaultPreset;
    dispatch(
      changeTableFilters(DASHBOARD_TABLE, {
        dateFrom: selectedPeriod.start,
        dateTo: selectedPeriod.end,
        preset: selectedPeriod.value,
      })
    );
  };

  return {
    isLoading,
    onChangePeriod,
    data,
    period: filters.preset,
    periods: presets.map(
      ({ label: title, value }: Preset): Option => ({ title, value })
    ),
  };
};
